import React, { useEffect, useState } from 'react';
import { Modal } from '../../../../../../ui/modal/modal';
import { Skill } from '../../../../../../utils/types/JobOpenings';

type SkillWeightingProps = {
  title: string;
  attributeName: string;
  isWeightRequired?: boolean;
  extractedSkill: Skill[];
  setExtractedSkill: (
      value: ((prevState: Skill[]) => Skill[]) | Skill[]
  ) => void;
  weightingEnabled: boolean;
  setWeightingEnabled: (
      value: ((prevState: boolean) => boolean) | boolean
  ) => void;
  similarEnabled: boolean;
  setSimilarEnabled: (
      value: ((prevState: boolean) => boolean) | boolean
  ) => void;
};

type SortOption = 'default' | 'highest' | 'lowest';

const DUMMY_SIMILAR_TERMS: Record<string, string[]> = {
  // Skills
  javascript: ['js', 'node.js', 'ecmascript'],
  react: ['react.js', 'reactjs'],
  python: ['py', 'python3'],
  html: ['html5'],
  css: ['css3', 'scss', 'sass'],
  java: ['j2ee', 'openjdk'],
  sql: ['mysql', 'postgresql', 't-sql'],
  git: ['github', 'gitlab', 'version control'],
  mongo: ['mongodb', 'nosql'],
  aws: ['amazon web services', 'cloud'],
  docker: ['containerization', 'containers'],
  typescript: ['ts'],

  // Job Titles
  developer: ['software engineer', 'programmer', 'coder', 'swe'],
  'software engineer': ['developer', 'swe', 'sde', 'engineer'],
  manager: ['lead', 'director', 'supervisor', 'head'],
  'product manager': ['pm', 'product owner'],
  designer: ['ui/ux', 'creative director', 'artist'],

  // Behavioral Attributes
  teamwork: ['collaboration', 'cooperation', 'team player'],
  communication: ['verbal skills', 'written communication', 'articulation'],
  'attention to detail': ['precision', 'accuracy', 'meticulousness'],
  leadership: ['mentorship', 'management', 'guidance'],
  'problem solving': ['analytical skills', 'troubleshooting'],
  adaptability: ['flexibility', 'resilience'],

  // Keywords (often match skills/tools, but adding specifics)
  'b2b sales': ['business to business', 'enterprise sales'],
  saas: ['software as a service', 'cloud services'],
  agile: ['scrum', 'kanban', 'lean'],
};

const SkillWeighting = ({
  title,
  attributeName,
  isWeightRequired = false,
  extractedSkill,
  setExtractedSkill,
  weightingEnabled,
  setWeightingEnabled,
  similarEnabled,
  setSimilarEnabled,
}: SkillWeightingProps) => {
  const [newSkillName, setNewSkillName] = useState('');
  const [newSkillScore, setNewSkillScore] = useState(10);
  const [sortOption, setSortOption] = useState<SortOption>('default');
  const [toastMessage, setToastMessage] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Similar Terms State
  const [similarTermsMap, setSimilarTermsMap] = useState<Record<string, string[]>>(DUMMY_SIMILAR_TERMS);
  const [editingTerm, setEditingTerm] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const handleEditSimilarClick = (skillName: string) => {
    const lowerName = skillName.toLowerCase();
    const currentTerms = similarTermsMap[lowerName] || [];
    setEditingTerm(skillName);
    setEditValue(currentTerms.join(', '));
  };

  const handleSaveSimilarTerms = (closeModal: () => void) => {
    if (editingTerm) {
      const lowerName = editingTerm.toLowerCase();
      const newTerms = editValue
        .split(',')
        .map((term) => term.trim())
        .filter((term) => term.length > 0);

      setSimilarTermsMap((prev) => ({
        ...prev,
        [lowerName]: newTerms,
      }));
    }
    closeModal();
    setEditingTerm(null);
    setEditValue('');
  };

  useEffect(() => {
    if (!weightingEnabled) return;

    const totalScore = extractedSkill.reduce((sum, s) => sum + s.weight, 0);

    const updatedSkills = extractedSkill.map((s) => {
      const newPercent =
          totalScore > 0 ? Math.round((s.weight / totalScore) * 100) : 0;
      if (s.weightPercent !== newPercent) {
        return { ...s, weightPercent: newPercent };
      }
      return s;
    });

    const hasChanges = updatedSkills.some(
      (s, i) => s.weightPercent !== extractedSkill[i].weightPercent,
    );

    if (hasChanges) {
      setExtractedSkill(updatedSkills);
    }
  }, [extractedSkill, weightingEnabled]);

  const addSkill = () => {
    const trimmedName = newSkillName.trim();
    if (!trimmedName || newSkillScore < 0) return;

    const isDuplicate = extractedSkill.some(
      (skill) => skill.skill.toLowerCase() === trimmedName.toLowerCase(),
    );
    if (isDuplicate) {
      setToastMessage(`"${trimmedName}" is already added.`);
      setTimeout(() => setToastMessage(''), 3000);
      return;
    }

    const updated = [
      ...extractedSkill,
      { skill: trimmedName, weight: newSkillScore, weightPercent: 0 },
    ];
    setExtractedSkill(updated);
    setNewSkillName('');
    setNewSkillScore(10);
  };

  const updateScore = (index: number, newScore: number) => {
    const updated = [...extractedSkill];
    updated[index].weight = newScore;
    setExtractedSkill(updated);
  };

  const updateSkillName = (index: number, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed) return;

    const isDuplicate = extractedSkill.some(
      (skill, i) =>
        i !== index && skill.skill.toLowerCase() === trimmed.toLowerCase(),
    );
    if (isDuplicate) {
      setToastMessage(`"${trimmed}" is already added.`);
      setTimeout(() => setToastMessage(''), 3000);
      return;
    }

    const updated = [...extractedSkill];
    updated[index].skill = trimmed;
    setExtractedSkill(updated);
  };

  const removeSkillByName = (skill: string) => {
    const updated = extractedSkill.filter((s) => s.skill !== skill);
    setExtractedSkill(updated);
  };

  const toggleSortOption = () => {
    setSortOption((prev) =>
      prev === 'default' ? 'highest' : prev === 'highest' ? 'lowest' : 'default',
    );
  };

  const maxCharacters =
      weightingEnabled && isWeightRequired ? 20 : 25;

  const upArrowColor =
      sortOption === 'highest' ? 'text-green-600' : 'text-gray-400';
  const downArrowColor =
      sortOption === 'lowest' ? 'text-green-600' : 'text-gray-400';

  const truncateSkillName = (name: string) => {
    return name.length > maxCharacters
      ? `${name.slice(
        0,
        weightingEnabled && isWeightRequired ? 20 : 25,
      )}...`
      : name;
  };

  const getSimilarTerms = (skillName: string) => {
    const lowerName = skillName.toLowerCase();
    return similarTermsMap[lowerName]?.join(', ') || '';
  };

  const getCategoryTheme = () => {
    const t = title.toLowerCase();
    const attr = attributeName.toLowerCase();
    if (t.includes('x:') || attr.includes('skill')) {
      return {
        badge: 'X AXIS',
        badgeBg: 'bg-emerald-50 text-emerald-700 border-emerald-200/80',
        dotBg: 'bg-emerald-500',
        quickSuggestions: ['React', 'JavaScript', 'TypeScript', 'Node.js', 'Python', 'SQL'],
        emptyDescription: 'Add skills to project candidates on the X-axis',
        icon: (
          <svg className='w-4 h-4 text-emerald-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13 10V3L4 14h7v7l9-11h-7z' />
          </svg>
        ),
      };
    }
    if (t.includes('y:') || attr.includes('job') || attr.includes('title')) {
      return {
        badge: 'Y AXIS',
        badgeBg: 'bg-blue-50 text-blue-700 border-blue-200/80',
        dotBg: 'bg-blue-500',
        quickSuggestions: ['Software Engineer', 'Frontend Developer', 'Product Manager', 'Team Lead'],
        emptyDescription: 'Add job titles for vertical match scoring',
        icon: (
          <svg className='w-4 h-4 text-blue-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' />
          </svg>
        ),
      };
    }
    if (t.includes('z:') || attr.includes('attribute') || attr.includes('behavior')) {
      return {
        badge: 'Z AXIS',
        badgeBg: 'bg-purple-50 text-purple-700 border-purple-200/80',
        dotBg: 'bg-purple-500',
        quickSuggestions: ['Problem Solving', 'Teamwork', 'Communication', 'Attention to Detail'],
        emptyDescription: 'Add behavioral traits for 3D depth analysis',
        icon: (
          <svg className='w-4 h-4 text-purple-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' />
          </svg>
        ),
      };
    }
    return {
      badge: 'KEYWORDS',
      badgeBg: 'bg-amber-50 text-amber-700 border-amber-200/80',
      dotBg: 'bg-amber-500',
      quickSuggestions: ['Agile', 'B2B', 'SaaS', 'Cloud', 'Microservices'],
      emptyDescription: 'Add weighted keywords for frequency scoring',
      icon: (
        <svg className='w-4 h-4 text-amber-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 20l4-16m2 16l4-16M6 9h14M4 15h14' />
        </svg>
      ),
    };
  };

  const theme = getCategoryTheme();

  const addSpecificSkill = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const isDuplicate = extractedSkill.some(
      (skill) => skill.skill.toLowerCase() === trimmed.toLowerCase(),
    );
    if (isDuplicate) return;
    setExtractedSkill([
      ...extractedSkill,
      { skill: trimmed, weight: 10, weightPercent: 0 },
    ]);
  };

  const cleanTitle = title.replace(/^[XYZ]:\s*/i, '');

  return (
    <div className='w-full h-full flex flex-col justify-between bg-white border border-gray-200 rounded-xl p-3.5 shadow-xs transition-shadow hover:shadow-sm'>
      { toastMessage && (
        <div className='fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-red-500 text-white text-xs px-3 py-1.5 rounded-md shadow-lg z-50 animate-bounce'>
          { toastMessage }
        </div>
      ) }

      { /* Header with aligned title, count badge, and toggles */ }
      <div className='flex items-center justify-between gap-2 pb-2.5 mb-2 border-b border-gray-100 min-h-[36px]'>
        <div className='flex items-center gap-1.5 min-w-0'>
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold border tracking-wider shrink-0 ${theme.badgeBg}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${theme.dotBg}`}></span>
            { theme.badge }
          </span>
          <span className='text-xs font-bold text-gray-800 tracking-tight truncate'>
            { cleanTitle }
          </span>
          <span className={`inline-flex items-center px-1.5 py-0.2 rounded-full text-[10px] font-semibold border shrink-0 ${
            extractedSkill.length > 0
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200/80 font-bold'
              : 'bg-gray-100 text-gray-500 border-gray-200/60'
          }`}>
            { extractedSkill.length }
          </span>
        </div>

        <div className='flex items-center gap-2.5 shrink-0'>
          { isWeightRequired && (
            <div className='flex items-center gap-1.5 relative group'>
              <span className='text-[11px] font-medium text-gray-600'>Weight</span>
              <button
                  type='button'
                  onClick={() => setWeightingEnabled(!weightingEnabled)}
                  className={`w-7 h-4 flex items-center rounded-full transition-colors duration-200 p-0.5 ${
                    weightingEnabled ? 'bg-emerald-600' : 'bg-gray-200'
                  }`}
                  title='Assign relative importance to each item'
              >
                <div
                    className={`bg-white w-3 h-3 rounded-full shadow-xs transform transition-transform duration-200 ${
                      weightingEnabled ? 'translate-x-3' : 'translate-x-0'
                    }`}
                />
              </button>
            </div>
          ) }

          { weightingEnabled && isWeightRequired && (
            <button
                type='button'
                onClick={toggleSortOption}
                className='p-0.5 text-gray-400 hover:text-emerald-700 hover:bg-emerald-50 rounded transition-colors'
                title={`Sort: ${sortOption === 'highest' ? 'Highest first' : sortOption === 'lowest' ? 'Lowest first' : 'Default'}`}
            >
              <span className='inline-flex items-center text-xs font-bold'>
                <span className={sortOption === 'highest' ? 'text-emerald-600 font-extrabold' : 'text-gray-400'}>↑</span>
                <span className={sortOption === 'lowest' ? 'text-emerald-600 font-extrabold' : 'text-gray-400'}>↓</span>
              </span>
            </button>
          ) }

          <div className='flex items-center gap-1.5 relative group'>
            <span className='text-[11px] font-medium text-gray-600'>Similar</span>
            <button
                type='button'
                onClick={() => setSimilarEnabled(!similarEnabled)}
                className={`w-7 h-4 flex items-center rounded-full transition-colors duration-200 p-0.5 ${
                  similarEnabled ? 'bg-emerald-600' : 'bg-gray-200'
                }`}
                title={`Allow matching with similar or closely related ${attributeName}s`}
            >
              <div
                  className={`bg-white w-3 h-3 rounded-full shadow-xs transform transition-transform duration-200 ${
                    similarEnabled ? 'translate-x-3' : 'translate-x-0'
                  }`}
              />
            </button>
          </div>
        </div>
      </div>

      { /* Skill List */ }
      <div className='flex-1 min-h-[140px] max-h-[220px] overflow-y-auto space-y-1.5 pr-1 custom-scrollbar'>
        { extractedSkill.length === 0 ? (
          <div className='h-full min-h-[135px] flex flex-col items-center justify-center py-2.5 text-center px-2'>
            <div className='w-7 h-7 rounded-full bg-gray-50 border border-gray-100 flex items-center justify-center mb-1 shadow-2xs'>
              { theme.icon }
            </div>
            <p className='text-xs font-semibold text-gray-700 mb-0.5'>No { attributeName }s added yet</p>
            <p className='text-[10px] text-gray-400 mb-2 max-w-[200px] leading-tight'>{ theme.emptyDescription }</p>

            { /* Quick Add Suggestions */ }
            <div className='flex flex-wrap gap-1 justify-center max-w-[280px]'>
              { theme.quickSuggestions
                .filter((s) => !extractedSkill.some((x) => x.skill.toLowerCase() === s.toLowerCase()))
                .slice(0, 4)
                .map((suggestion) => (
                  <button
                      key={suggestion}
                      type='button'
                      onClick={() => addSpecificSkill(suggestion)}
                      className='inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-50 hover:bg-emerald-50 text-gray-600 hover:text-emerald-700 border border-gray-200/80 hover:border-emerald-300 transition-all hover:scale-105 active:scale-95'
                      title={`Quick add "${suggestion}"`}
                  >
                    <span className='text-emerald-600 font-bold'>+</span> { suggestion }
                  </button>
                )) }
            </div>
          </div>
        ) : (
          [...extractedSkill]
            .sort((a, b) => {
              if (sortOption === 'highest') return b.weight - a.weight;
              if (sortOption === 'lowest') return a.weight - b.weight;
              return 0;
            })
            .map((skill, index) => (
              <div
                  key={index}
                  className='flex items-center justify-between gap-2 bg-gray-50/70 hover:bg-gray-50 border border-gray-200/70 hover:border-gray-300 rounded-lg px-2.5 py-1.5 transition-all group'
              >
                <div className='flex flex-col flex-1 min-w-0 mr-2'>
                  <div className='relative group/tooltip w-full'>
                    <input
                        type='text'
                        value={skill.skill}
                        readOnly={editingIndex !== index}
                        autoFocus={editingIndex === index}
                        onClick={() => setEditingIndex(index)}
                        onChange={(e) => {
                          if (editingIndex === index) {
                            const updated = [...extractedSkill];
                            updated[index].skill = e.target.value;
                            setExtractedSkill(updated);
                          }
                        }}
                        onBlur={() => {
                          if (editingIndex === index) {
                            updateSkillName(index, skill.skill);
                            setEditingIndex(null);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && editingIndex === index) {
                            updateSkillName(index, skill.skill);
                            setEditingIndex(null);
                          }
                        }}
                        className={`text-xs font-medium rounded px-1 py-0.5 w-full transition-colors ${
                          editingIndex !== index
                            ? 'bg-transparent border border-transparent cursor-pointer truncate hover:text-emerald-700 text-gray-800'
                            : 'bg-white border border-emerald-500 ring-2 ring-emerald-100 text-gray-900 outline-none'
                        }`}
                    />

                    { /* Tooltip for truncated text */ }
                    { skill.skill.length > maxCharacters &&
                      editingIndex !== index && (
                      <div className='absolute z-10 left-0 bottom-full mb-1 hidden group-hover/tooltip:block bg-gray-900 text-white text-[11px] px-2 py-0.5 rounded shadow-lg whitespace-nowrap pointer-events-none'>
                        { skill.skill }
                      </div>
                    ) }
                  </div>

                  { /* Similar Terms Display */ }
                  { similarEnabled && (
                    <div className='flex items-center mt-0.5 px-1 group/similar relative'>
                      <svg className='w-2.5 h-2.5 text-gray-400 mr-1 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z' />
                      </svg>
                      <span className='text-[10px] text-gray-500 truncate mr-1.5' title={getSimilarTerms(skill.skill)}>
                        { getSimilarTerms(skill.skill) || <span className='italic opacity-75'>No similar terms</span> }
                      </span>

                      <button
                          type='button'
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditSimilarClick(skill.skill);
                          }}
                          className='opacity-0 group-hover/similar:opacity-100 transition-opacity bg-white hover:bg-emerald-50 text-gray-400 hover:text-emerald-600 p-0.5 rounded border border-gray-200'
                          title='Edit similar terms'
                      >
                        <svg className='w-2.5 h-2.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z' />
                        </svg>
                      </button>
                    </div>
                  ) }
                </div>

                <div className='flex items-center gap-1.5 flex-shrink-0'>
                  { weightingEnabled && isWeightRequired && (
                    <input
                        type='number'
                        min={0}
                        value={skill.weight}
                        onChange={(e) =>
                          updateScore(index, Number(e.target.value))
                        }
                        onBlur={() => {
                          const updated = [...extractedSkill];
                          setExtractedSkill(updated);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const updated = [...extractedSkill];
                            setExtractedSkill(updated);
                          }
                        }}
                        className='w-12 text-xs font-semibold text-center border border-gray-200 rounded px-1 py-0.5 bg-white text-gray-800 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none'
                        title='Weight'
                    />
                  ) }
                  { weightingEnabled && isWeightRequired && (
                    <span className='text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200/80 px-1.5 py-0.5 rounded min-w-[32px] text-center'>
                      { skill.weightPercent }%
                    </span>
                  ) }
                  <button
                      type='button'
                      onClick={() => removeSkillByName(skill.skill)}
                      className='w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0'
                      aria-label='Remove'
                      title='Remove'
                  >
                    <svg className='w-3 h-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                    </svg>
                  </button>
                </div>
              </div>
            ))
        ) }
      </div>

      { /* Add Skill Row */ }
      <div className='mt-2.5 pt-2 border-t border-gray-100 flex items-center gap-1.5'>
        <div className='flex-1 flex items-center gap-1.5 bg-gray-50/80 hover:bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1 focus-within:bg-white focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-100 transition-all'>
          <svg className='w-3.5 h-3.5 text-gray-400 shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' />
          </svg>
          <input
              type='text'
              placeholder={`Add ${attributeName}...`}
              value={newSkillName}
              onChange={(e) => setNewSkillName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addSkill();
                }
              }}
              onBlur={addSkill}
              className='text-xs bg-transparent outline-none w-full text-gray-800 placeholder-gray-400'
          />
        </div>

        { weightingEnabled && isWeightRequired && (
          <div className='flex items-center gap-1 shrink-0 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1' title='Default weight score'>
            <span className='text-[10px] text-gray-400 font-semibold'>wt:</span>
            <input
                type='number'
                min={0}
                value={newSkillScore}
                onChange={(e) => setNewSkillScore(Number(e.target.value))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addSkill();
                  }
                }}
                className='w-10 text-xs font-semibold text-center border-0 bg-transparent text-gray-800 focus:outline-none'
            />
          </div>
        ) }

        <button
            type='button'
            onClick={addSkill}
            disabled={!newSkillName.trim()}
            className='px-3 py-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg transition-all shrink-0 shadow-xs'
        >
          Add
        </button>
      </div>

      { /* Edit Similar Terms Modal */ }
      <Modal
          visible={!!editingTerm}
          toggle={() => setEditingTerm(null)}
          headerText={`Edit Similar Terms for "${editingTerm}"`}
          customClass='!max-w-md !h-auto'
          modalContent={(closeModal) => (
            <div className='flex flex-col gap-4'>
              <div className='bg-blue-50 text-blue-800 p-3 rounded-md text-sm border border-blue-100'>
                Add synonyms or related terms separated by commas. These will be used to broaden the search matching.
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Similar Terms (comma separated)
                </label>
                <textarea
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className='w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none'
                    rows={4}
                    placeholder='e.g. js, node.js, ecmascript'
                />
              </div>

              <div className='flex justify-end gap-3 mt-2'>
                <button
                    onClick={closeModal}
                    className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                >
                  Cancel
                </button>
                <button
                    onClick={() => handleSaveSimilarTerms(closeModal)}
                    className='px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                >
                  Save Changes
                </button>
              </div>
            </div>
          )}
      />
    </div>
  );
};

export default SkillWeighting;
